import { Path, isEmbedPurchaseFlowPath } from 'constants/router'
import { AnalyticsEvent } from './AnalyticsEvent'
import { NO_FLOW_VERSION } from './pfFlowVersion'
import { logAnalyticsEvent } from './analyticsUtils'

export type OrderFlowSurface = 'order_flow' | 'status_view'

/** Closed set: free text must never reach the providers. The first three are also embed_status_card_shown.errorReason's. */
export type OrderFlowLoadFailureReason =
  | 'MISSING_PARAMS'
  | 'STATUS_FETCH_FAILED'
  | 'ORDER_NOT_FOUND'
  | 'BOOTSTRAP_MISSING'
  | 'BOOTSTRAP_REJECTED'
  | 'RENDER_ERROR'
  | 'PROPERTY_FETCH_FAILED'

export type RenderErrorSource = 'boundary' | 'window_error' | 'unhandled_rejection'

export type BootstrapMissingCause = 'no_token' | 'reload' | 'back_forward' | 'stripe_return' | 'session_expired'

interface PreScreenLoadFailure {
  failureReason: Extract<OrderFlowLoadFailureReason, 'BOOTSTRAP_MISSING' | 'BOOTSTRAP_REJECTED' | 'RENDER_ERROR'>
  httpStatus?: number | null
  errorSource?: RenderErrorSource
  missingCause?: BootstrapMissingCause
}

const reportedSurfaces = new Set<OrderFlowSurface>()
let launchAttemptId: string | null = null
let orderFlowChosen = false

export const embedLoadSurfaceForPath = (pathname: string): OrderFlowSurface | null => {
  if (pathname.toLowerCase().replace(/\/+$/, '') === Path.EMBED_STATUS_CARD) return 'status_view'
  return isEmbedPurchaseFlowPath(pathname) ? 'order_flow' : null
}

export const markOrderFlowLoadReported = (surface: OrderFlowSurface): void => {
  reportedSurfaces.add(surface)
}

export const isOrderFlowLoadReported = (surface: OrderFlowSurface): boolean => reportedSurfaces.has(surface)

export const rememberBootstrapLaunchAttemptId = (id: string | null): void => {
  launchAttemptId = id
}

export const markOrderFlowChosen = (): void => {
  orderFlowChosen = true
}

/** Sends a failure that happened before any screen rendered; returns whether it was sent. */
export const reportPreScreenLoadFailure = ({ failureReason, httpStatus, errorSource, missingCause }: PreScreenLoadFailure): boolean => {
  const surface = embedLoadSurfaceForPath(window.location.pathname)
  if (!surface || isOrderFlowLoadReported(surface)) return false
  markOrderFlowLoadReported(surface)
  logAnalyticsEvent(
    AnalyticsEvent.ORDER_FLOW_LOADED,
    {
      surface,
      load_status: 'failure',
      failure_reason: failureReason,
      first_screen: 'error',
      ...(httpStatus !== undefined ? { http_status: httpStatus } : {}),
      ...(errorSource ? { error_source: errorSource } : {}),
      ...(missingCause ? { missing_cause: missingCause } : {}),
      ...(launchAttemptId ? { launch_attempt_id: launchAttemptId } : {}),
      ...(surface === 'order_flow' && !orderFlowChosen ? { version: NO_FLOW_VERSION } : {})
    },
    { transport: 'beacon' }
  )
  return true
}

export const _resetOrderFlowLoadForTests = (): void => {
  reportedSurfaces.clear()
  launchAttemptId = null
  orderFlowChosen = false
}
